import React from 'react'
import { Box, FormControlLabel, IconButton, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'

const ZOOMS = [
  { days: 1, label: '1 day' },
  { days: 2, label: '2 days' },
  { days: 4, label: '4 days' }
]

const idOf = (text) => (text === '' ? '' : Number(text))

// The toolbar of the screen (maqueta `.toolbar`): period arrows with the range, grouping, the two filters, zoom, the
// suspicious switch and the one that shows the finished jobs. It owns no state: the page decides what each control does. `disabled` locks it all while a correction is
// being applied (RN66).
//   by machine: «Machine» must be chosen (no «All machines»), «Worker» offers «All» (RN9, GATE2-G12);
//   by worker: the other way round.
const TimelineToolbar = ({ query, zoomDays, onlySuspicious, showFinished = false, deviceOptions, workerOptions, canGoNext, disabled = false, onPrevious, onNext, onView, onFilters, onZoom, onOnlySuspicious, onShowFinished = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { formatDate } = useDateFormatter()
  const byDevice = query.view === 'device'

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'center', p: 1.25, mb: 1.25, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <IconButton size='small' aria-label={t_('Previous period')} disabled={disabled} onClick={onPrevious}><ChevronLeft /></IconButton>
        <Typography component='span' data-testid='period-range' sx={{ fontWeight: 500 }}>
          {`${formatDate(query.from)} – ${formatDate(query.to)}`}
        </Typography>
        <IconButton size='small' aria-label={t_('Next period')} disabled={disabled || !canGoNext} onClick={onNext}><ChevronRight /></IconButton>
      </Box>
      <Box>
        <Typography variant='caption' component='div' color='text.secondary'>{t_('Group by')}</Typography>
        <ToggleButtonGroup size='small' exclusive value={query.view} aria-label={t_('Group by')} disabled={disabled} onChange={(_event, view) => { if (view) onView(view) }}>
          <ToggleButton value='device'>{t_('Machine')}</ToggleButton>
          <ToggleButton value='worker'>{t_('Worker')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TextField
        select size='small' label={t_('Machine')} value={String(query.deviceId)} disabled={disabled} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        onChange={(event) => onFilters({ deviceId: idOf(event.target.value) })}
      >
        {byDevice
          ? <option value='' disabled>{t_('Choose a machine')}</option>
          : <option value=''>{t_('All machines')}</option>}
        {deviceOptions.map((device) => <option key={device.id} value={String(device.id)}>{device.label}</option>)}
      </TextField>
      <TextField
        select size='small' label={t_('Worker')} value={String(query.workerId)} disabled={disabled} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        onChange={(event) => onFilters({ workerId: idOf(event.target.value) })}
      >
        {byDevice
          ? <option value=''>{t_('All')}</option>
          : <option value='' disabled>{t_('Choose a worker')}</option>}
        {workerOptions.map((worker) => <option key={worker.id} value={String(worker.id)}>{worker.label}</option>)}
      </TextField>
      <TextField
        select size='small' label={t_('Zoom')} value={String(zoomDays)} disabled={disabled} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        onChange={(event) => onZoom(Number(event.target.value))}
      >
        {ZOOMS.map((zoom) => <option key={zoom.days} value={String(zoom.days)}>{t_(zoom.label)}</option>)}
      </TextField>
      <FormControlLabel
        disabled={disabled}
        control={<Switch checked={onlySuspicious} onChange={(event) => onOnlySuspicious(event.target.checked)} />}
        label={t_('Only suspicious clock-ins (more than 10 h)')}
      />
      {/* PO 2026-10-17: the jobs finished during the whole period are hidden unless this is on. */}
      <FormControlLabel
        disabled={disabled}
        control={<Switch checked={showFinished} onChange={(event) => onShowFinished(event.target.checked)} />}
        label={t_('Show finished jobs')}
      />
    </Box>
  )
}

export default TimelineToolbar
